// ============================================================
// frontend/src/pages/Analytics/AnalyticsDashboard.js
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box, Typography, Paper, Tabs, Tab, Select, MenuItem,
  FormControl, InputLabel, LinearProgress, Chip, Button,
  CircularProgress, Alert, Tooltip, Divider, Skeleton,
  Card, CardContent, CardActionArea
} from '@mui/material';
import {
  BarChart3, TrendingUp, TrendingDown, Target, AlertTriangle,
  Award, Users, Clock, CheckCircle, XCircle, ArrowRight,
  ChevronDown, Minus, Zap, BookOpen
} from 'lucide-react';
import FeatureGate from '../../components/subscription/FeatureGate';
import UpgradePrompt from '../../components/subscription/UpgradePrompt';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// ---- Shared styles ----
const cardSx = {
  p: 2.5, border: '1px solid', borderColor: 'divider',
  borderRadius: 2, height: '100%'
};

// ---- Summary Cards ----
function SummaryRow({ summary }) {
  if (!summary) return null;
  const items = [
    { label: 'Questions', value: summary.totalAnswered, icon: BookOpen, color: '#6C63FF' },
    { label: 'Accuracy', value: `${summary.overallAccuracy}%`, icon: Target, color: summary.overallAccuracy >= 70 ? '#4caf50' : summary.overallAccuracy >= 50 ? '#ff9800' : '#f44336' },
    { label: 'Avg Time', value: `${summary.avgTimePerQuestion}s`, icon: Clock, color: '#2196f3' },
    { label: 'Topics', value: summary.topicsAttempted, icon: BarChart3, color: '#9c27b0' }
  ];

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
      {items.map(item => (
        <Paper key={item.label} elevation={0} sx={{ ...cardSx, flex: '1 1 130px', minWidth: 130, maxWidth: 200, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <item.icon size={20} color={item.color} />
          <Typography variant="h5" fontWeight={700} sx={{ color: item.color }}>{item.value}</Typography>
          <Typography variant="caption" color="text.secondary">{item.label}</Typography>
        </Paper>
      ))}
    </Box>
  );
}

// ---- Topic Breakdown ----
function TopicBreakdown({ topics, onTopicClick }) {
  if (!topics || topics.length === 0) {
    return <Typography variant="body2" color="text.secondary">No topic data yet. Answer some questions to see your breakdown.</Typography>;
  }

  return (
    <Box>
      {topics.map(t => (
        <CardActionArea key={t.topic} onClick={() => onTopicClick(t.topic)} sx={{ mb: 1.5, borderRadius: 2 }}>
          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>{t.topic}</Typography>
                <Chip
                  label={t.strength}
                  size="small"
                  sx={{
                    bgcolor: t.strength === 'strong' ? '#e8f5e9' : t.strength === 'moderate' ? '#fff3e0' : '#ffebee',
                    color: t.strength === 'strong' ? '#2e7d32' : t.strength === 'moderate' ? '#e65100' : '#c62828',
                    fontWeight: 600, fontSize: '0.7rem'
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={700}>{t.accuracy}%</Typography>
                <ArrowRight size={14} color="#999" />
              </Box>
            </Box>
            <LinearProgress
              variant="determinate" value={t.accuracy}
              sx={{
                height: 6, borderRadius: 3, bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  bgcolor: t.strength === 'strong' ? '#4caf50' : t.strength === 'moderate' ? '#ff9800' : '#f44336',
                  borderRadius: 3
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              {t.correct}/{t.total} correct · avg {t.avgTime}s
            </Typography>
          </Paper>
        </CardActionArea>
      ))}
    </Box>
  );
}

// ---- Difficulty Breakdown ----
function DifficultyBreakdown({ data }) {
  if (!data || data.length === 0) return null;

  // Group by topic
  const byTopic = {};
  data.forEach(d => {
    if (!byTopic[d.topic]) byTopic[d.topic] = {};
    byTopic[d.topic][d.difficulty] = d;
  });

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Accuracy by Difficulty</Typography>
      {Object.entries(byTopic).slice(0, 8).map(([topic, diffs]) => (
        <Box key={topic} sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>{topic}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {['easy', 'medium', 'hard'].map(level => {
              const d = diffs[level];
              if (!d) return <Box key={level} sx={{ flex: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, textAlign: 'center' }}><Typography variant="caption" color="text.secondary">{level}: —</Typography></Box>;
              return (
                <Box key={level} sx={{
                  flex: 1, p: 1, borderRadius: 1, textAlign: 'center',
                  bgcolor: d.accuracy >= 70 ? '#e8f5e9' : d.accuracy >= 50 ? '#fff3e0' : '#ffebee'
                }}>
                  <Typography variant="caption" display="block" color="text.secondary">{level}</Typography>
                  <Typography variant="body2" fontWeight={700}>{d.accuracy}%</Typography>
                  <Typography variant="caption" color="text.secondary">({d.total})</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ---- Time Trends ----
function TimeTrends({ trends }) {
  if (!trends || trends.length === 0) {
    return <Typography variant="body2" color="text.secondary">Study for a few weeks to see trends.</Typography>;
  }

  const maxTotal = Math.max(...trends.map(t => t.total), 1);

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Weekly Trends</Typography>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', height: 120, mb: 1 }}>
        {trends.map((week, i) => (
          <Tooltip key={i} title={`${week.weekLabel}: ${week.total} questions, ${week.accuracy}% accuracy, ${week.studyDays} days`}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{week.accuracy}%</Typography>
              <Box sx={{
                width: '100%', maxWidth: 40,
                height: `${(week.total / maxTotal) * 100}%`,
                minHeight: 4, borderRadius: '4px 4px 0 0',
                bgcolor: week.accuracy >= 70 ? '#4caf50' : week.accuracy >= 50 ? '#ff9800' : '#f44336',
                transition: 'height 0.3s'
              }} />
            </Box>
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">{trends[0]?.weekLabel}</Typography>
        <Typography variant="caption" color="text.secondary">{trends[trends.length - 1]?.weekLabel}</Typography>
      </Box>
    </Box>
  );
}

// ---- Strengths & Weaknesses ----
function StrengthsWeaknesses({ data }) {
  if (!data) return null;
  const { strengths, weaknesses } = data;

  const renderList = (items, isStrength) => (
    <Box sx={{ flex: 1, minWidth: 200 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {isStrength ? <CheckCircle size={18} color="#4caf50" /> : <AlertTriangle size={18} color="#f44336" />}
        <Typography variant="subtitle2" fontWeight={600}>
          {isStrength ? 'Strongest Topics' : 'Needs Work'}
        </Typography>
      </Box>
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Answer at least 10 questions per topic</Typography>
      ) : items.map(item => (
        <Paper key={item.topic} elevation={0} sx={{ p: 1.5, mb: 1, bgcolor: isStrength ? '#f1f8e9' : '#fce4ec', borderRadius: 1.5 }}>
          <Typography variant="body2" fontWeight={600}>{item.topic}</Typography>
          <Typography variant="caption" color="text.secondary">
            {item.overallAccuracy}% overall · {item.recentAccuracy !== null ? `${item.recentAccuracy}% recent` : 'no recent data'} · {item.total} questions
          </Typography>
        </Paper>
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      {renderList(strengths, true)}
      {renderList(weaknesses, false)}
    </Box>
  );
}

// ---- Improvement Velocity ----
function ImprovementVelocity({ data }) {
  if (!data || data.length === 0) {
    return <Typography variant="body2" color="text.secondary">Study across two months to see improvement trends.</Typography>;
  }

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>Month-over-Month Improvement</Typography>
      {data.map(item => (
        <Box key={item.topic} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
          {item.change > 0 ? <TrendingUp size={18} color="#4caf50" /> :
           item.change < 0 ? <TrendingDown size={18} color="#f44336" /> :
           <Minus size={18} color="#999" />}
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={500}>{item.topic}</Typography>
            <Typography variant="caption" color="text.secondary">
              {item.previousAccuracy}% → {item.currentAccuracy}%
            </Typography>
          </Box>
          <Chip
            label={`${item.change > 0 ? '+' : ''}${item.change}%`}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: item.change > 0 ? '#e8f5e9' : item.change < 0 ? '#ffebee' : '#f5f5f5',
              color: item.change > 0 ? '#2e7d32' : item.change < 0 ? '#c62828' : '#666'
            }}
          />
        </Box>
      ))}
    </Box>
  );
}

// ---- Mistake Patterns ----
function MistakePatterns({ data }) {
  if (!data || data.totalAnalyzed === 0) {
    return <Typography variant="body2" color="text.secondary">No incorrect answers to analyze yet — keep going!</Typography>;
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Analysed {data.totalAnalyzed} incorrect answers
      </Typography>
      {data.patterns.filter(p => p.count > 0 && p.type !== 'other').slice(0, 5).map(pattern => (
        <Paper key={pattern.type} elevation={0} sx={{ p: 2, mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>{pattern.label}</Typography>
            <Chip label={`${pattern.count} (${pattern.percentage}%)`} size="small" color="error" variant="outlined" />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {pattern.description}
          </Typography>
          <Alert severity="info" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
            <strong>Tip:</strong> {pattern.tip}
          </Alert>
          {pattern.examples.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Examples:</Typography>
              {pattern.examples.slice(0, 2).map((ex, i) => (
                <Typography key={i} variant="caption" display="block" sx={{ ml: 1, color: 'text.secondary' }}>
                  • {ex.topic}: you answered "{ex.userAnswer}", correct was "{ex.correctAnswer}"
                </Typography>
              ))}
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  );
}

// ---- Benchmarks ----
function Benchmarks({ data }) {
  if (!data) return null;
  if (!data.available) {
    return <Alert severity="info">{data.reason}</Alert>;
  }

  const PercentileBar = ({ label, value }) => (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" fontWeight={700}>Top {100 - value}%</Typography>
      </Box>
      <LinearProgress
        variant="determinate" value={value}
        sx={{
          height: 8, borderRadius: 4, bgcolor: 'action.hover',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            bgcolor: value >= 75 ? '#4caf50' : value >= 50 ? '#2196f3' : '#ff9800'
          }
        }}
      />
    </Box>
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Compared against {data.usersInGrade} students in Grade {data.grade}
      </Typography>
      <PercentileBar label="Accuracy" value={data.overall.accuracyPercentile} />
      <PercentileBar label="Questions Answered" value={data.overall.volumePercentile} />

      {data.topicPercentiles.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>By Topic</Typography>
          {data.topicPercentiles.map(tp => (
            <Box key={tp.topic} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ flex: 1 }}>{tp.topic}</Typography>
              <Chip
                label={`Top ${100 - tp.percentile}%`}
                size="small"
                sx={{
                  fontWeight: 600,
                  bgcolor: tp.percentile >= 75 ? '#e8f5e9' : tp.percentile >= 50 ? '#e3f2fd' : '#fff3e0',
                  color: tp.percentile >= 75 ? '#2e7d32' : tp.percentile >= 50 ? '#1565c0' : '#e65100'
                }}
              />
              <Typography variant="caption" color="text.secondary">{tp.accuracy}%</Typography>
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}

// ---- Main Dashboard ----
function AnalyticsDashboardContent() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState(0);
  const [grade, setGrade] = useState(user?.profile?.grade || 8);
  const [weeks, setWeeks] = useState(8);
  const [dashboard, setDashboard] = useState(null);
  const [mistakes, setMistakes] = useState(null);
  const [benchmarks, setBenchmarks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/analytics/dashboard?grade=${grade}&weeks=${weeks}`, { headers });
      const data = await res.json();
      if (data.success) setDashboard(data.data);
      else setError('Failed to load analytics');
    } catch (e) { setError('Failed to load analytics'); }
    setLoading(false);
  }, [grade, weeks, token]);

  const fetchMistakes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/mistakes?grade=${grade}`, { headers });
      const data = await res.json();
      if (data.success) setMistakes(data.data);
    } catch (e) { /* non-fatal */ }
  }, [grade, token]);

  const fetchBenchmarks = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/analytics/benchmarks?grade=${grade}`, { headers });
      const data = await res.json();
      if (data.success) setBenchmarks(data.data);
    } catch (e) { /* non-fatal */ }
  }, [grade, token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { if (tab === 2) fetchMistakes(); }, [tab, fetchMistakes]);
  useEffect(() => { if (tab === 3) fetchBenchmarks(); }, [tab, fetchBenchmarks]);

  const handleTopicClick = (topic) => {
    // Could navigate to a topic detail page or open a modal
    console.log('Topic clicked:', topic);
  };

  const tabLabels = ['Overview', 'Topics', 'Mistakes', 'Benchmarks'];

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <BarChart3 size={28} color="#6C63FF" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Deep insight into your performance</Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Grade</InputLabel>
          <Select value={grade} label="Grade" onChange={e => setGrade(e.target.value)}>
            {[8, 9, 10, 11, 12].map(g => <MenuItem key={g} value={g}>Grade {g}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Period</InputLabel>
          <Select value={weeks} label="Period" onChange={e => setWeeks(e.target.value)}>
            <MenuItem value={4}>4 weeks</MenuItem>
            <MenuItem value={8}>8 weeks</MenuItem>
            <MenuItem value={12}>12 weeks</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
        variant="scrollable" scrollButtons="auto">
        {tabLabels.map(label => <Tab key={label} label={label} sx={{ textTransform: 'none' }} />)}
      </Tabs>

      {loading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading analytics...</Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && dashboard && (
        <>
          {/* Tab 0: Overview */}
          {tab === 0 && (
            <Box>
              <SummaryRow summary={dashboard.summary} />
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Paper elevation={0} sx={{ ...cardSx, flex: '1 1 350px' }}>
                  <TimeTrends trends={dashboard.timeTrends} />
                </Paper>
                <Paper elevation={0} sx={{ ...cardSx, flex: '1 1 350px' }}>
                  <StrengthsWeaknesses data={dashboard.strengthsWeaknesses} />
                </Paper>
              </Box>
              <Paper elevation={0} sx={{ ...cardSx, mt: 3 }}>
                <ImprovementVelocity data={dashboard.improvementVelocity} />
              </Paper>
            </Box>
          )}

          {/* Tab 1: Topics */}
          {tab === 1 && (
            <Box>
              <TopicBreakdown topics={dashboard.topicBreakdown} onTopicClick={handleTopicClick} />
              <Divider sx={{ my: 3 }} />
              <DifficultyBreakdown data={dashboard.difficultyBreakdown} />
            </Box>
          )}

          {/* Tab 2: Mistakes */}
          {tab === 2 && <MistakePatterns data={mistakes} />}

          {/* Tab 3: Benchmarks */}
          {tab === 3 && <Benchmarks data={benchmarks} />}
        </>
      )}

      {!loading && !dashboard && !error && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 3 }}>
          <BarChart3 size={48} color="#ccc" />
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>No data yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Answer some questions on the ForYou or Following feeds to start seeing your analytics.
          </Typography>
        </Paper>
      )}
    </Box>
  );
}

export default function AnalyticsDashboard() {
  return (
    <FeatureGate feature="detailed_analytics" fallback={<UpgradePrompt feature="detailed_analytics" />}>
      <AnalyticsDashboardContent />
    </FeatureGate>
  );
}
